import { Link } from "react-router-dom";
import ModuleSkeleton from "@/components/app/ModuleSkeleton";
import { Card } from "@/components/ui/card";
import { Sparkles, Brain, MessageSquare, FileSearch, BarChart3, ShieldCheck, Wand2, Bot, ArrowRight } from "lucide-react";

const COPILOTS = [
  { name: "HR Policy Copilot",    desc: "Answers any employee policy question grounded in your handbook.",  icon: ShieldCheck, status: "Live",        link: "/ai" },
  { name: "Performance Copilot",  desc: "Drafts review summaries, flags off-track goals, suggests feedback.", icon: Brain,       status: "Beta",        link: "/ai" },
  { name: "Recruiter Copilot",    desc: "Ranks resumes, drafts outreach, suggests interview questions.",     icon: FileSearch,  status: "Coming Soon", link: "#"   },
  { name: "Manager Copilot",      desc: "Weekly digest of team mood, blockers, and 1:1 talking points.",     icon: BarChart3,   status: "Coming Soon", link: "#"   },
  { name: "Payroll Copilot",      desc: "Explains payslips, detects anomalies, suggests adjustments.",       icon: Wand2,       status: "Coming Soon", link: "#"   },
  { name: "Comms Copilot",        desc: "Drafts announcements, recognition messages, and offer letters.",    icon: MessageSquare, status: "Beta",       link: "/ai" },
];

export default function AICopilots() {
  return (
    <ModuleSkeleton
      testid="ai-copilots-page"
      title="AI Copilots"
      subtitle="Specialised Claude 4.5 copilots embedded in every workflow. Grounded in your tenant's data — never the public internet."
      icon={Bot}
      badge="Beta"
      tagline="Your private AI workforce"
      features={[
        { title: "Tenant-grounded", description: "Each copilot only sees your tenant data. No cross-tenant leakage.",   icon: ShieldCheck },
        { title: "Audit-logged",    description: "Every prompt, response, and source citation is recorded.",            icon: FileSearch  },
        { title: "Human-in-loop",   description: "AI suggests; humans approve. Configurable per copilot.",              icon: Sparkles    },
      ]}
      capabilities={[
        "RAG over your handbook, policies, payroll, and tickets",
        "Claude 4.5 Sonnet model under the hood",
        "Cost guardrails per tenant per month",
        "PII redaction before sending to LLM",
        "Custom system prompts per role",
        "Slack / Teams / Email / In-app delivery",
      ]}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {COPILOTS.map((c) => {
          const Body = (
            <Card className="p-5 border border-slate-200 hover:border-violet-300 hover:shadow-md transition group h-full">
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 text-white flex items-center justify-center group-hover:scale-105 transition">
                  <c.icon className="w-5 h-5" />
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
                  c.status === "Live" ? "bg-emerald-100 text-emerald-700" :
                  c.status === "Beta" ? "bg-violet-100 text-violet-700" :
                  "bg-amber-100 text-amber-700"
                }`}>{c.status}</span>
              </div>
              <div className="font-semibold text-slate-900 text-sm">{c.name}</div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">{c.desc}</div>
              {c.link !== "#" && (
                <div className="text-xs text-violet-700 font-semibold mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Try it <ArrowRight className="w-3 h-3" />
                </div>
              )}
            </Card>
          );
          return c.link === "#"
            ? <div key={c.name} data-testid={`copilot-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>{Body}</div>
            : <Link key={c.name} to={c.link} data-testid={`copilot-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>{Body}</Link>;
        })}
      </div>
    </ModuleSkeleton>
  );
}
